import { ErrorHandler } from '@sketch/tracing'
import { groupBy } from '@sketch/utils'

/**
 * Normalizes a node or ID reference to just the ID
 */
function getNodeId<P extends Record<string, unknown>>(
  nodeOrId: NodeOrId<P>
): string {
  return typeof nodeOrId === 'string' ? nodeOrId : nodeOrId.id
}

export interface TreeNodeData<Payload extends Record<string, unknown>> {
  id: string
  // We are allowing to load tree bit by bit, therefore looking just at the loaded nodes is not enough
  // to tell if a node is a leaf (meaning that it doesn't have any children).
  // We ask the caller to explicitly mark nodes as leaves.
  //
  // So far we are using this flag to determine if a node can be opened or not.
  hasChildren: boolean
  payload: Payload | null
}

const ROOT_ID = 'root'
const RootNode = { id: ROOT_ID, payload: null, hasChildren: true } as const

export interface SetChildrenTreeItemParams {
  id: string
  parentId: string | null
  hasChildren: boolean
}

type NodeOrId<P extends Record<string, unknown>> = string | TreeNodeData<P>

/**
 * Base tree implementation providing core tree operations
 */
class TreeBase<Payload extends Record<string, unknown>> {
  static ROOT_ID = ROOT_ID

  /**
   * Holds the actual node data by node.id.
   */
  private nodes: Map<string, TreeNodeData<Payload>> = new Map()

  /**
   * For each nodeId → parentId (null if it's the root)
   */
  private parentMap: Map<string, string | null> = new Map()

  /**
   * For each parentId → array of child node IDs
   */
  private childrenMap: Map<string, string[]> = new Map()

  constructor() {
    this.reset()
  }

  private initRootNode(): void {
    this.createNode(RootNode)
    this.setParentId(ROOT_ID, null)
  }

  private reset(): void {
    this.nodes.clear()
    this.parentMap.clear()
    this.childrenMap.clear()
    this.initRootNode()
  }

  /**
   * Checks if the node is a leaf (no children expected).
   * @returns `true` if leaf, `false` if not leaf, `null` if node not found
   */
  protected isLeaf(id: string): boolean | null {
    const node = this.getNode(id)
    if (!node) return null
    return !node.hasChildren
  }

  /**
   * Creates a new node and ensures `childrenMap` is initialized.
   */
  protected createNode(node: TreeNodeData<Payload>): TreeNodeData<Payload> {
    const id = node.id
    this.nodes.set(id, node)

    // Initialize children array for the newly created node if it doesn't exist
    if (!this.childrenMap.has(id)) this.childrenMap.set(id, [])

    return node
  }

  protected setParentId(
    childId: NodeOrId<Payload>,
    parentId: string | null
  ): void {
    this.parentMap.set(getNodeId(childId), parentId)
  }

  /**
   * Replaces a node's children IDs with a new array.
   */
  protected setChildrenIds(
    parentId: NodeOrId<Payload>,
    newChildrenIds: string[]
  ): void {
    this.childrenMap.set(getNodeId(parentId), newChildrenIds)
  }

  /**
   * Gets all descendant node IDs of the specified parent(s).
   */
  protected getAllDescendantIds(
    parentIds: NodeOrId<Payload> | Array<NodeOrId<Payload>>
  ): string[] {
    // Normalize input into an array of string IDs
    const initialIds: string[] = Array.isArray(parentIds)
      ? parentIds.map(p => getNodeId(p))
      : [getNodeId(parentIds)]

    // Prepare visited set and work stack
    const visited = new Set<string>(initialIds)
    const stack = [...initialIds]
    const allChildren: string[] = []

    // Iteratively walk the tree
    while (stack.length > 0) {
      const current = stack.pop()!
      const children = this.getChildrenIds(current)

      for (const childId of children) {
        if (!visited.has(childId)) {
          visited.add(childId)
          allChildren.push(childId)
          stack.push(childId)
        }
      }
    }

    return allChildren
  }

  /**
   * Delete nodes and all their descendants (excluding root)
   */
  protected deleteSubtree(ids: string[]): void {
    if (ids.length === 0) return

    // If ROOT_ID is included, we can reset the entire tree
    if (ids.includes(ROOT_ID)) {
      this.reset()
      return
    }

    const nodesWithChildren = this.getAllDescendantIds(ids)
    const allIds = new Set(
      [...ids, ...nodesWithChildren].filter(x => x !== ROOT_ID)
    )

    // Process each node only once
    for (const id of allIds) {
      const parentId = this.getParentId(id)

      // Remove the node from its parent's children, if parent exists
      if (parentId) {
        const siblings = this.getChildrenIds(parentId)
        const newSiblings = siblings.filter(sibling => sibling !== id)
        this.setChildrenIds(parentId, newSiblings)
      }

      // Clean up all references to the node
      this.nodes.delete(id)
      this.parentMap.delete(id)
      this.childrenMap.delete(id)
    }
  }

  /**
   * Throws away existing children for a given parent and sets new children.
   */
  setChildren(
    parentId: string | null,
    children: TreeNodeData<Payload>[]
  ): void {
    const actualParentId = parentId ?? ROOT_ID
    const parentNode = this.getNode(actualParentId)
    if (!parentNode) {
      throw new Error(`Parent node with id "${actualParentId}" not found`)
    }

    if (children.some(child => child.id === ROOT_ID)) {
      throw new Error('Cannot add root node as a child')
    }

    // Remove existing children from the parent
    this.deleteSubtree(this.getChildrenIds(parentNode))

    // Add new children
    const newChildrenIds: string[] = []
    for (const childData of children) {
      const childNode = this.createNode(childData)
      this.setParentId(childNode.id, actualParentId)
      newChildrenIds.push(childNode.id)
    }

    this.setChildrenIds(actualParentId, newChildrenIds)
  }

  setChildrenTree<T extends Payload>(args: {
    list: T[]
    itemParamsMap: Map<T, SetChildrenTreeItemParams>
  }) {
    const { list, itemParamsMap } = args

    // Group items by their parent ID
    const groupedByParentId = groupBy(list, x => {
      const params = itemParamsMap.get(x)
      return params?.parentId || ROOT_ID
    })

    const allNodeIds = list.map(item => itemParamsMap.get(item)?.id || ROOT_ID)

    const groupsCount = Object.keys(groupedByParentId).length
    // Early return for empty case
    if (groupsCount === 0) return this

    // Cleanup existing nodes - we are going to replace them either way
    // However, we need to have a clean state before we start adding new nodes
    // so that we wouldn't add nodes to parent node which is going to be deleted with the next iteration
    this.deleteSubtree(allNodeIds)

    for (let i = 0; i < groupsCount; i++) {
      // pick first group whose parent is already in the tree
      let existingParentId: string | undefined
      for (const parentId in groupedByParentId) {
        if (!this.getNode(parentId)) continue
        existingParentId = parentId
        break
      }

      if (!existingParentId) {
        ErrorHandler.shouldNeverHappen(
          'There are non-root groups without a parent'
        )
        return this
      }

      // set children for the existing parent
      const children = groupedByParentId[existingParentId]
      this.setChildren(
        existingParentId,
        children.map(payload => {
          const itemParams = itemParamsMap.get(payload)
          if (!itemParams) {
            ErrorHandler.shouldNeverHappen(
              `Item params not found for the current tree item: ${JSON.stringify(
                payload
              )}`
            )
            return { id: '', payload, hasChildren: false }
          }

          const { id, hasChildren } = itemParams
          return { id, payload, hasChildren }
        })
      )

      // remove the processed group
      delete groupedByParentId[existingParentId]
    }
  }

  /**
   * Returns all leaf nodes (no children) under the given parent (recursively).
   */
  getLeafNodesFromParent(parentId: string): TreeNodeData<Payload>[] {
    const parent = this.getNode(parentId)
    if (!parent) return []

    const leafNodes: TreeNodeData<Payload>[] = []
    const stack: string[] = [parentId]

    while (stack.length) {
      const nodeId = stack.pop()!
      const node = this.getNode(nodeId)
      if (!node) continue

      const childrenIds = this.getChildrenIds(node)
      if (childrenIds.length === 0) {
        // This node has no children -> leaf
        leafNodes.push(node)
      } else {
        stack.push(...childrenIds)
      }
    }

    return leafNodes
  }

  // Helper methods
  getNode(id: string | undefined): TreeNodeData<Payload> | undefined {
    if (!id) return undefined
    return this.nodes.get(id)
  }

  getRoot(): TreeNodeData<Payload> {
    return this.getNode(ROOT_ID)!
  }

  getChildren(id: NodeOrId<Payload> | undefined): TreeNodeData<Payload>[] {
    if (!id) return []
    return this.getChildrenIds(id)
      .map(childId => this.nodes.get(childId))
      .filter((child): child is TreeNodeData<Payload> => !!child)
  }

  getChildrenIds(id: NodeOrId<Payload>): string[] {
    const nodeId = typeof id === 'string' ? id : id.id
    return this.childrenMap.get(nodeId) ?? []
  }

  /**
   * @returns
   *  - `undefined` if the node is not found
   *  - `null` if the node is the root
   */
  getParent(
    id: NodeOrId<Payload> | undefined
  ): TreeNodeData<Payload> | undefined | null {
    if (!id) return undefined
    const parentId = this.getParentId(id)
    if (parentId === null) return null
    if (!parentId) return undefined

    return this.getNode(parentId)
  }

  /**
   * @returns
   *  - `undefined` if the node doesn't exist in `parentMap`
   *  - `null` if the node is the root
   */
  getParentId(id: NodeOrId<Payload>): string | null | undefined {
    const nodeId = typeof id === 'string' ? id : id.id
    return this.parentMap.get(nodeId)
  }

  // Navigation methods
  getNextSibling(id: string): TreeNodeData<Payload> | undefined {
    const parentId = this.getParentId(id)
    if (!parentId) return undefined // either root or doesn't exist

    const siblings = this.getChildrenIds(parentId)
    const index = siblings.indexOf(id)
    return index < siblings.length - 1
      ? this.getNode(siblings[index + 1])
      : undefined
  }

  getPrevSibling(id: string): TreeNodeData<Payload> | undefined {
    const node = this.getNode(id)
    if (!node) return undefined

    const parentId = this.getParentId(id)
    if (!parentId) return undefined

    const siblings = this.getChildrenIds(parentId)
    const index = siblings.indexOf(id)
    return index > 0 ? this.getNode(siblings[index - 1]) : undefined
  }

  getFirstChild(id: string): TreeNodeData<Payload> | undefined {
    const children = this.getChildrenIds(id)
    if (!children.length) return undefined
    return this.getNode(children[0])
  }

  getLastChild(id: string): TreeNodeData<Payload> | undefined {
    const children = this.getChildrenIds(id)
    if (!children.length) return undefined
    return this.getNode(children[children.length - 1])
  }
}

export class CollapsibleTree<
  Payload extends {} = {},
> extends TreeBase<Payload> {
  private expandedIds: Set<string> = new Set([ROOT_ID])

  setOpen(id: string | string[]) {
    const ids = typeof id === 'string' ? [id] : id
    for (const id of ids) {
      if (this.isLeaf(id)) continue
      this.expandedIds.add(id)
    }
  }

  setClosed(id: string | string[]) {
    const ids = typeof id === 'string' ? [id] : id
    for (const id of ids) {
      if (this.isLeaf(id)) continue
      this.expandedIds.delete(id)
    }
  }

  setIsOpen(id: string, isOpen: boolean) {
    if (isOpen) {
      this.setOpen(id)
    } else {
      this.setClosed(id)
    }
  }

  isOpen(id: string): boolean {
    return this.expandedIds.has(id)
  }

  getParentsPathToNode(id: string): string[] {
    const currentNode = this.getNode(id)
    if (!currentNode) return []

    // construct the path to the current node
    const parentsPath: string[] = []
    for (
      let parent = this.getParent(currentNode);
      parent;
      parent = this.getParent(parent)
    ) {
      parentsPath.push(parent.id)
    }
    parentsPath.reverse()
    return parentsPath
  }

  getPathToNode(id: string): string[] {
    return [...this.getParentsPathToNode(id), id]
  }

  isVisible(id: string): boolean {
    const parentsPath = this.getParentsPathToNode(id)
    return parentsPath.every(nodeId => this.isOpen(nodeId))
  }

  getFirstVisibleParent(id: string): TreeNodeData<Payload> | null {
    const parentsPath = this.getParentsPathToNode(id)
    if (!parentsPath.length) return null

    const firstVisibleParentId = parentsPath.find(p => !this.isOpen(p))

    return this.getNode(firstVisibleParentId) ?? null
  }

  getNextVisibleNode(id: string): TreeNodeData<Payload> | undefined {
    // if the current node is not visible, walk back up the tree
    // until we find a visible ancestor
    const node = this.isVisible(id)
      ? this.getNode(id)
      : this.getFirstVisibleParent(id)

    if (!node) return undefined

    // If node is expanded and has children, return first child
    if (this.isOpen(node.id) && this.getChildrenIds(node).length) {
      return this.getChildren(node)[0]
    }

    let currentNode = node
    // Try to get next sibling or its first expanded descendant
    while (currentNode) {
      const nextSibling = this.getNextSibling(currentNode.id)
      if (nextSibling) return nextSibling
      currentNode = this.getParent(currentNode)!
    }

    return undefined
  }

  /**
   * Gets the previous visible node in a depth-first walk, skipping collapsed subtrees.
   * Returns `null` if there's a collapsed ancestor that is the closest "visible" parent.
   */
  getPrevVisibleNode(id: string): TreeNodeData<Payload> | undefined | null {
    // If the current node is not visible, return the first visible parent (if any).
    if (!this.isVisible(id)) {
      return this.getFirstVisibleParent(id)
    }

    const node = this.getNode(id)
    if (!node) return undefined

    // If there's a previous sibling, return it or its deepest expanded descendant.
    const prevSibling = this.getPrevSibling(node.id)
    if (prevSibling) {
      if (this.isOpen(prevSibling.id)) {
        return this.getLastExpandedDescendant(prevSibling.id)
      }
      return prevSibling
    }

    // If no previous sibling, go up to the parent (unless the parent is root).
    const parentId = this.getParentId(node.id)
    if (parentId === ROOT_ID || parentId === null) {
      return undefined
    }
    if (parentId === undefined) {
      // parent not found, or node doesn't exist in parentMap
      return undefined
    }

    return this.getNode(parentId)
  }

  /**
   * Note: expanded !== visible
   * There can be a node that is expanded (or its parent is expanded), but still not visible
   * because one of its ancestors is collapsed
   */
  private getLastExpandedDescendant(
    id: string
  ): TreeNodeData<Payload> | undefined {
    const node = this.getNode(id)
    if (!node || !this.isOpen(id) || !this.getChildrenIds(node).length) {
      return node
    }

    const lastChild = this.getLastChild(id)
    if (!lastChild) return node

    return this.getLastExpandedDescendant(lastChild.id)
  }

  getFirstVisibleNode(): TreeNodeData<Payload> | undefined {
    return this.getFirstChild(ROOT_ID)
  }

  getLastVisibleNode(): TreeNodeData<Payload> | undefined {
    return this.getLastExpandedDescendant(ROOT_ID)
  }
}
